// AES-GCM encryption via the Web Crypto API. The decryption key never
// leaves the browser as part of a request: it lives only in the URL
// fragment (the part after #), which browsers never send to any server 
// so even this site's own backend (Firestore) only ever stores ciphertext.
const textToBuffer = (text) => new TextEncoder().encode(text)
const bufferToText = (buffer) => new TextDecoder().decode(buffer)

const bufferToBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  bytes.forEach((b) => {
    binary += String.fromCharCode(b)
  })
  return window
    .btoa(binary)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

const base64UrlToBuffer = (base64url) => {
  const padded = base64url.padEnd(
    base64url.length + ((4 - (base64url.length % 4)) % 4),
    '=',
  )
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/')
  const binary = window.atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes.buffer
}

export const encryptText = async (plainText) => {
  const key = await window.crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt'],
  )
  const iv = window.crypto.getRandomValues(new Uint8Array(12))
  const encrypted = await window.crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    textToBuffer(plainText),
  )
  const rawKey = await window.crypto.subtle.exportKey('raw', key)

  return {
    ciphertext: bufferToBase64Url(encrypted),
    iv: bufferToBase64Url(iv.buffer),
    keyBase64Url: bufferToBase64Url(rawKey),
  }
}

export const decryptText = async ({ ciphertext, iv, keyBase64Url }) => {
  const key = await window.crypto.subtle.importKey(
    'raw',
    base64UrlToBuffer(keyBase64Url),
    { name: 'AES-GCM' },
    false,
    ['decrypt'],
  )
  const decrypted = await window.crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: base64UrlToBuffer(iv) },
    key,
    base64UrlToBuffer(ciphertext),
  )
  return bufferToText(decrypted)
}
